<template>
  <div 
    class="confirmation-container d-flex align-items-center justify-content-center"
    :class="{ 'compact-view': asistenciaConfirmada || showQRCode }"
  >
    <div 
      class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
      :class="{ 'compact-card': asistenciaConfirmada || showQRCode }"
    >
      <!-- Vista de QR confirmado -->
      <div v-if="asistenciaConfirmada" class="qr-view text-center">
        <p class="qr-header">Pase Digital</p>
        
        <div class="qr-wrapper">
          <img v-if="qrCode" :src="qrCode" alt="Código QR" class="qr-code">
          <p v-else class="qr-error-message">No se ha generado el código QR</p>
        </div>
        
        <img 
          src="@/assets/corona3.png" 
          alt="Decoración corona"
          class="decoration-crown"
        >
        
        <p class="event-title">Mis XV Johanna</p>
        <p class="qr-instructions">Preséntalo el día del evento</p>
      </div>

      <!-- Formulario de confirmación -->
      <div v-else>
        <div class="form-header">
          <h2 class="form-title">Confirma Asistencia</h2>
          <p class="form-subtitle">Por favor completa tus datos</p>
        </div>
        
        <form @submit.prevent="handleSubmit" class="confirmation-form">
          <!-- Campo Nombre -->
          <div class="form-group">
            <label for="name" class="form-label">Nombre</label>
            <input
              id="name"
              v-model.trim="name"
              type="text"
              class="form-control"
              placeholder="Ingresa tu nombre completo"
              required
            >
          </div>

          <!-- Campo Teléfono -->
          <div class="form-group">
            <label for="phone" class="form-label">Teléfono</label>
            <input
              id="phone"
              v-model.trim="phone"
              type="tel"
              class="form-control"
              placeholder="Ingresa tu teléfono"
              required
              pattern="[0-9]{10}"
              title="Ingresa un número de 10 dígitos"
            >
          </div>

          <!-- Selector de Invitado por (solo si no es XV) -->
          <div v-if="!isXvEvent" class="form-group">
            <label class="form-label">¿Invitado por?</label>
            <div class="radio-group">
              <div class="radio-option">
                <input
                  id="novia"
                  v-model="invitedBy"
                  type="radio"
                  value="novia"
                  class="radio-input"
                  required
                >
                <label for="novia" class="radio-label">Novia</label>
              </div>
              <div class="radio-option">
                <input
                  id="novio"
                  v-model="invitedBy"
                  type="radio"
                  value="novio"
                  class="radio-input"
                >
                <label for="novio" class="radio-label">Novio</label>
              </div>
            </div>
          </div>

          <!-- Selector de Asistencia -->
          <div class="form-group">
            <label class="form-label">¿Asistirás?</label>
            <div class="radio-group">
              <div class="radio-option">
                <input
                  id="si"
                  v-model="attending"
                  type="radio"
                  value="si"
                  class="radio-input"
                  required
                >
                <label for="si" class="radio-label">Sí</label>
              </div>
              <div class="radio-option">
                <input
                  id="no"
                  v-model="attending"
                  type="radio"
                  value="no"
                  class="radio-input"
                >
                <label for="no" class="radio-label">No</label>
              </div>
            </div>
          </div>

          <!-- Selector de Número de Invitados -->
          <div class="form-group">
            <label for="guests" class="form-label">¿Cuántos asisten?</label>
            <select
              id="guests"
              v-model="guests"
              class="form-select"
              :disabled="attending === 'no' || loading"
              required
            >
              <option value="" disabled selected>Selecciona una opción</option>
              <option
                v-for="n in guestsOptions"
                :key="n"
                :value="n"
              >
                {{ n }}
              </option>
              <option v-if="attending === 'no'" value="0">0</option>
            </select>
            <div v-if="loading" class="loading-indicator">Cargando opciones...</div>
          </div>
          
          <!-- Botón de Confirmación -->
          <button type="submit" class="submit-button" :disabled="isSubmitting">
            <span v-if="!isSubmitting">Confirmar</span>
            <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </button>
          
          <!-- Mensajes de estado -->
          <div v-if="errorMessage" class="alert alert-danger mt-3">
            {{ errorMessage }}
          </div>
        </form>

        <!-- Vista de QR después de confirmar -->
        <div v-if="showQRCode" class="qr-view text-center">
          <p class="confirmation-thanks">¡Gracias por confirmar!</p>
          <p class="qr-header">Pase Digital</p>
          
          <div class="qr-wrapper">
            <img v-if="qrCode" :src="qrCode" alt="Código QR" class="qr-code">
            <p v-else class="qr-error-message">No se ha generado el código QR</p>
          </div>
          
          <img 
            src="@/assets/separador.png" 
            alt="Decoración corona"
            class="decoration-crown"
          >
          
          <p class="event-title">Mis XV Johanna</p>
          <p class="qr-instructions">Preséntalo el día del evento</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import axios from 'axios';
import baseUrl from '../api';

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: {
      type: Number,
      required: true,
      validator: value => Number.isInteger(value) && value > 0
    },
    nombre: {
      type: String,
      default: '',
    },
    idFestejado: {
      type: Number,
      default: null,
    }
  },
  setup(props) {
    // Estados del formulario
    const name = ref('');
    const phone = ref('');
    const invitedBy = ref('');
    const attending = ref('');
    const guests = ref('');
    const guestsOptions = ref([1, 2]);
    const loading = ref(true);
    const isSubmitting = ref(false);
    const errorMessage = ref('');
    
    // Estados de la aplicación
    const qrCode = ref(null);
    const showQRCode = ref(false);
    const asistenciaConfirmada = ref(false);
    
    // Computed properties
    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase();
      return ['xv', 'ym'].includes(eventName);
    });

    // Métodos
    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`
        );
        
        const numPases = response.data.num_pase;
        guestsOptions.value = numPases > 0 
          ? Array.from({ length: numPases }, (_, i) => i + 1)
          : [1, 2];
          
      } catch (error) {
        console.error('Error al obtener límite de invitados:', error);
        errorMessage.value = 'Error al cargar las opciones de invitados';
      } finally {
        loading.value = false;
      }
    };
    
    const verificarAsistencia = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/verificar-asistencia`, {
          idInvitado: props.idInvitado
        });
        
        if (response.data.asistio) {
          asistenciaConfirmada.value = true;
          await obtenerCodigoQR();
        }
      } catch (error) {
        console.error('Error al verificar asistencia:', error);
      }
    };
    
    const obtenerCodigoQR = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/codigoqrinvitado`, { 
          id_invitado: Number(props.idInvitado)
        });
        
        qrCode.value = response.data?.codigoqr || null;
      } catch (error) {
        console.error('Error obteniendo código QR:', error);
        errorMessage.value = 'Error al generar el código QR';
      }
    };
    
    const handleSubmit = async () => {
      if (attending.value === 'no') {
        guests.value = '0';
      }
      
      isSubmitting.value = true;
      errorMessage.value = '';
      
      try {
        const formData = {
          nombre_asistencia: name.value.trim(),
          telefono_invitado: phone.value.trim(),
          invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
          asistira: attending.value,
          id_festejada: props.idFestejado,
          id_invitado: props.idInvitado,
          num_pases: attending.value === 'no' ? 0 : Number(guests.value),
        };

        await axios.post(`${baseUrl}/asistencia`, formData);
        
        await obtenerCodigoQR();
        showQRCode.value = true;
        
      } catch (error) {
        console.error('Error confirmando asistencia:', error);
        errorMessage.value = 'Hubo un error al confirmar. Por favor intenta nuevamente.';
      } finally {
        isSubmitting.value = false;
      }
    };

    // Watchers
    watch(attending, (newValue) => {
      if (newValue === 'no') {
        guests.value = '0';
      } else if (newValue === 'si' && guests.value === '0') {
        guests.value = '1';
      }
    });

    // Lifecycle hooks
    onMounted(() => {
      fetchGuestLimit();
      verificarAsistencia();
    });

    return {
      name,
      phone,
      invitedBy,
      attending,
      guests,
      guestsOptions,
      loading,
      isSubmitting,
      errorMessage,
      isXvEvent,
      asistenciaConfirmada,
      qrCode,
      showQRCode,
      handleSubmit
    };
  }
};
</script>

<style scoped>
/* Estilos base */
.confirmation-container {
  min-height: 100vh;
  padding: 2rem 1rem;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
}

.confirmation-card {
  width: 100%;
  max-width: 500px;
  background: white;
  background: linear-gradient(to bottom, #ffffff, #f9f9f9);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

/* Vista compacta para QR */
.compact-view {
  min-height: 70vh;
  align-items: flex-start;
  padding-top: 4rem;
}

.compact-card {
  max-width: 400px;
  padding: 2rem;
}

/* Encabezado del formulario */
.form-header {
  margin-bottom: 2rem;
  text-align: center;
}

.form-title {
   font-size: 20px ! important;
    color: #4b0808;
  font-family: 'british', sans-serif;
  margin-bottom: 0.5rem;
}

.form-subtitle {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 1rem;
}

/* Grupos de formulario */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: 'Karla', sans-serif;
  font-weight: 500;
  color: #495057;
}

/* Controles de formulario */
.form-control,
.form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background-color: rgba(255, 255, 255, 0.8);
  transition: border-color 0.3s, box-shadow 0.3s;
}

.form-control:focus,
.form-select:focus {
  border-color: #d592b0;
  box-shadow: 0 0 0 0.25rem rgba(213, 146, 176, 0.25);
  outline: none;
}

/* Radio buttons */
.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-option {
  display: flex;
  align-items: center;
}

.radio-input {
  margin-right: 0.5rem;
  accent-color: #d592b0;
}

.radio-label {
  font-family: 'Karla', sans-serif;
  color: #495057;
}

/* Botón de enviar */
.submit-button {
  width: 100%;
  padding: 0.75rem;
  font-family: 'FeelingPassionate', cursive;
  font-size: 1.25rem;
  color: white;
  background-color: #4b0602f4;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-button:hover {
  background-color: #c07d9b;
}

.submit-button:disabled {
  background-color: #d592b0;
  opacity: 0.7;
  cursor: not-allowed;
}

/* Vista QR */
.qr-view {
  padding: 1rem;
}

.qr-header {
  font-family: 'Westony', cursive;
  font-size: 1.75rem;
  color: #5a3d5c;
  margin-bottom: 1.5rem;
}

.confirmation-thanks {
  font-family: 'Karla', sans-serif;
  font-weight: 600;
  color: #354257;
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.qr-wrapper {
  margin: 1rem 0;
}

.qr-code {
  width: 180px;
  height: 180px;
  border: 8px solid white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.decoration-crown {
  width: auto;
  height: 80px;
  margin: 0.5rem 0;
}

.event-title {
  font-family: 'Parisienne', cursive;
  font-size: 1.75rem;
  color: #5a3d5c;
  margin: 0.5rem 0;
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 1rem;
  margin-top: 1rem;
}

/* Mensajes de error */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}

.qr-error-message {
  color: #dc3545;
  font-weight: 500;
}

.loading-indicator {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.5rem;
}

/* Responsive */
@media (max-width: 576px) {
  .confirmation-card {
    padding: 1.5rem;
  }
  
  .form-title {
    font-size: 1.75rem;
  }
  
  .radio-group {
    gap: 1rem;
  }
}
</style>